import React, {useState, useRef} from 'react';
import MainLayout from '../../../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {tr} from '../../../../../translations';
import {getByScreenSize} from '../../../../../utils/responsive';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import TextGeneric from '../../../../../components/textGeneric';
import GenericButton from '../../../../../components/button';
import DatePicker from '../../../../../components/datePicker';
import {convertToYYYYMMDD} from '../../../../../utils/helpers';
import RadioPicker from '../../../../../components/radioPicker';
import GenericInput from '../../../../../components/textInput';
import {ToastType} from '../../../../../utils/enums';
import {Theme} from '../../../../../theme/lightTheme';
import {ScrollView, View} from 'react-native';
import {showGlobalToast} from '../../../../../connected-components/toast/actions';
import {AddEditPatientProps} from '../../../../../interfaces/screens/addEditPatient';
import dayjs from 'dayjs';
import {createPatient, updatePatient} from '../actions';

// Styled components
const Container = styled.View`
  width: 100%;
  flex: 1;
  position: relative;
  padding-top: ${({theme}) => theme.text.s55 * 2}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: ${({theme}) => theme.text.s55}px;
`;

const FormContainer = styled.View`
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 10px;
  padding: ${({theme}) => theme.text.s55 * 2}px;
  shadow-opacity: 0.1;
  shadow-radius: 10px;
  shadow-color: ${({theme}) => theme.text.black};
  shadow-offset: 0px 2px;
  elevation: 2;
`;

const FormTitle = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s5}px;
  color: ${({theme}) => theme.text.title};
  width: 100%;
  text-align: center;
`;
const ButtonContainer = styled.View`
  align-items: flex-end;
  margin-top: ${({theme}) => theme.text.s55}px;
`;

const SaveButton = styled(GenericButton)`
  min-width: 150px;
`;

const Separator = styled.View`
  height: 1px;
  width: 100%;
  background-color: ${({theme}) => theme.border};
  margin-vertical: ${({theme}) => theme.text.s55}px;
`;

const TextInputItem = styled(GenericInput)`
  ${({multiline, theme}) =>
    multiline &&
    `
     padding-top: 3%;
  padding-bottom: 3%;
  height: ${theme.text.s1 * 3}px;
  align-items: flex-start;
  text-align-vertical: top;
    `}
`;

const GenericFieldsContainer = styled.View`
  width: 100%;
  padding-top: ${({theme}) => theme.text.s55 * 2}px;
`;

const FormScrollView = styled(ScrollView)`
  width: 100%;
`;

const FieldWrapper = styled(View)`
  margin-bottom: ${({theme}) => theme.text.s7}px;
`;

// Interface for patient form
interface PatientForm {
  name: string;
  email: string;
  dob: string;
  gender: string;
  address: string;
  phone: string;
}

/**
 * AddEditPatient screen component.
 *
 * @param {any} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const AddEditPatient: React.FC<AddEditPatientProps> = ({route}) => {
  const theme = useTheme() as Theme;
  const {patient, callback} = route?.params || {};
  const navigation = useNavigation<NavigationProp<any>>();
  const [formValues, setFormValues] = useState<PatientForm>({
    name: patient?.name || '',
    email: patient?.email || '',
    dob: patient?.dob ? dayjs(patient?.dob).format('YYYY-MM-DD') : '',
    gender: patient?.gender || '',
    address: patient?.address || '',
    phone: patient?.phone || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({
    name: false,
    email: false,
    dob: false,
    gender: false,
    address: false,
    phone: false,
  });
  const scrollRef = useRef<ScrollView>(null);

  // Handle input change
  const handleInputChange = (key: keyof PatientForm, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [key]: value,
    }));

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [key]: true,
    }));

    // Validate field on change
    validateField(key, value);
  };

  // Handle field blur
  const handleBlur = (key: keyof PatientForm) => {
    setTouched(prev => ({
      ...prev,
      [key]: true,
    }));

    // Validate on blur
    validateField(key, formValues[key]);
  };

  // Validate a single field
  const validateField = (key: keyof PatientForm, value: any) => {
    let fieldError = '';

    // Required field validation for name, email, and phone
    if ((key === 'name' || key === 'email' || key === 'phone') && !value) {
      fieldError = tr('patients.requiredField');
    }

    // Phone number validation
    if (key === 'phone' && value && !/^\d+$/.test(value)) {
      fieldError = tr('patients.phoneNumberError');
    }

    // Update errors state
    setErrors(prev => ({
      ...prev,
      [key]: fieldError,
    }));

    return fieldError === '';
  };

  // Validate all form fields
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Mark all fields as touched
    const newTouched: Record<string, boolean> = {
      name: true,
      email: true,
      dob: true,
      gender: true,
      address: true,
      phone: true,
    };
    setTouched(newTouched);

    // Required field validation for name, email, and phone
    if (!formValues.name) {
      newErrors.name = tr('patients.requiredField');
      isValid = false;
    }

    if (!formValues.email) {
      newErrors.email = tr('patients.requiredField');
      isValid = false;
    }

    if (!formValues.phone) {
      newErrors.phone = tr('patients.requiredField');
      isValid = false;
    } else if (!/^\d+$/.test(formValues.phone)) {
      newErrors.phone = tr('patients.phoneNumberError');
      isValid = false;
    }

    setErrors(newErrors);
    return {isValid, errors: newErrors};
  };

  // Scroll to the top of the page on error
  const scrollToTop = () => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({
          y: 0,
          animated: true,
        });
      }, 100);
    }
  };

  // Handle form submission
  const handleSavePatient = async () => {
    const {isValid} = validateForm();
    if (isValid) {
      let res = null;
      if (patient?.id) {
        res = await updatePatient(patient?.id, formValues);
      } else {
        res = await createPatient(formValues);
      }
      if (res) {
        callback?.();
        navigation.goBack();
      }
    } else {
      showGlobalToast({
        title: tr('toast.errorTitle'),
        message: tr('patients.validationError'),
        type: ToastType.HELP,
      });
      scrollToTop();
    }
  };

  return (
    <MainLayout
      enableScroll={false}
      title={tr('drawer.patients')}
      backHeader
      menuHeader
      noPadding>
      <FormScrollView
        ref={scrollRef}
        contentContainerStyle={{flexGrow: 1}}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={true}
        scrollEventThrottle={16}>
        <Container>
          <ContentContainer>
            <FormContainer>
              <FormTitle bold>{tr('patients.addPatient')}</FormTitle>

              <GenericFieldsContainer>
                {/* Name Field */}
                <FieldWrapper>
                  <TextInputItem
                    isLabelBold
                    label={tr('patients.patientName')}
                    placeholder={tr('patients.patientName')}
                    value={formValues.name}
                    onChangeText={(val: string) =>
                      handleInputChange('name', val)
                    }
                    onBlur={() => handleBlur('name')}
                    containerStyle={{
                      marginBottom:
                        getByScreenSize(theme.text.s7, theme.text.s9) / 2,
                    }}
                    isRequired={true}
                    hasError={!!errors.name && touched.name}
                    errorMessage={touched.name ? errors.name : ''}
                    enableErrorMessage={true}
                  />
                </FieldWrapper>
                <Separator />

                {/* Email Field */}
                <FieldWrapper>
                  <TextInputItem
                    isLabelBold
                    label={tr('patients.patientEmail')}
                    placeholder={tr('patients.patientEmail')}
                    value={formValues.email}
                    onChangeText={(val: string) =>
                      handleInputChange('email', val)
                    }
                    onBlur={() => handleBlur('email')}
                    containerStyle={{
                      marginBottom:
                        getByScreenSize(theme.text.s7, theme.text.s9) / 2,
                    }}
                    isRequired={true}
                    hasError={!!errors.email && touched.email}
                    errorMessage={touched.email ? errors.email : ''}
                    enableErrorMessage={true}
                  />
                </FieldWrapper>
                <Separator />

                {/* Date of Birth Field */}
                <FieldWrapper>
                  <DatePicker
                    isLabelBold
                    placeholder={tr('patients.dobPlaceholder')}
                    value={
                      formValues.dob
                        ? convertToYYYYMMDD(formValues.dob) || undefined
                        : undefined
                    }
                    //@ts-ignore
                    maxDate={new Date()}
                    label={tr('patients.patientDob')}
                    onValueChange={value => handleInputChange('dob', value)}
                    format={'YYYY-MM-DD'}
                    onBlur={() => handleBlur('dob')}
                  />
                </FieldWrapper>
                <Separator />

                {/* Gender Field */}
                <FieldWrapper>
                  <RadioPicker
                    label={tr('patients.patientGender')}
                    radioButtons={[
                      {
                        id: 'male',
                        label: tr('patients.male'),
                        containerStyle: {
                          minHeight: theme.text.s1,
                        },
                      },
                      {
                        id: 'female',
                        label: tr('patients.female'),
                        containerStyle: {
                          minHeight: theme.text.s1,
                        },
                      },
                      {
                        id: 'other',
                        label: tr('patients.other'),
                        containerStyle: {
                          minHeight: theme.text.s1,
                        },
                      },
                    ]}
                    onPress={val => handleInputChange('gender', val)}
                    selectedId={formValues.gender}
                  />
                </FieldWrapper>
                <Separator />

                {/* Phone Field */}
                <FieldWrapper>
                  <TextInputItem
                    isLabelBold
                    label={tr('patients.patientPhone')}
                    placeholder={tr('patients.patientPhone')}
                    value={formValues.phone}
                    onChangeText={(val: string) => {
                      // Only allow digits
                      if (val === '' || /^\d+$/.test(val)) {
                        handleInputChange('phone', val);
                      }
                    }}
                    onBlur={() => handleBlur('phone')}
                    keyboardType={'number-pad'}
                    containerStyle={{
                      marginBottom:
                        getByScreenSize(theme.text.s7, theme.text.s9) / 2,
                    }}
                    isRequired={true}
                    hasError={!!errors.phone && touched.phone}
                    errorMessage={touched.phone ? errors.phone : ''}
                    enableErrorMessage={true}
                  />
                </FieldWrapper>

                <Separator />

                {/* Address Field */}
                <FieldWrapper>
                  <TextInputItem
                    isLabelBold
                    label={tr('patients.patientAddress')}
                    placeholder={tr('patients.patientAddress')}
                    multiline={true}
                    scrollEnabled={true}
                    value={formValues.address}
                    onChangeText={(val: string) =>
                      handleInputChange('address', val)
                    }
                    onBlur={() => handleBlur('address')}
                    containerStyle={{
                      marginBottom:
                        getByScreenSize(theme.text.s7, theme.text.s9) / 2,
                    }}
                  />
                </FieldWrapper>
              </GenericFieldsContainer>

              <ButtonContainer>
                <SaveButton
                  label={tr('patients.savePatient')}
                  onPress={handleSavePatient}
                />
              </ButtonContainer>
            </FormContainer>
          </ContentContainer>
        </Container>
      </FormScrollView>
    </MainLayout>
  );
};

AddEditPatient.displayName = 'AddEditPatientScreen';
export default AddEditPatient;
